import type { ComponentItem } from '@/features/end-of-lease/types';
import { FileType } from '@/features/file-overview/types';

import type { ComponentDoc } from '@/lib/types';

// ARC docs for a component, optionally scoped to one serial. A null/empty serial
// means all serials (older links with no serial in the route).
export function arcDocumentsForSerial(
    docs: ComponentDoc[] | undefined,
    targetSerial?: string | null,
): ComponentDoc[] {
    return (docs ?? []).filter(
        (doc) =>
            doc.type === FileType.arc &&
            (!targetSerial || doc.serialNumber === targetSerial),
    );
}

// Key for the project's ARC counts, which arrive as one entry per part number / serial
// number pair. Uses a separator no part or serial number can contain, so two pairs can't
// collide on a joined string.
export function arcDocCountKey(partNumber: string, serialNumber: string): string {
    return `${partNumber}\u0000${serialNumber}`;
}

export interface Page<T> {
    total: number;
    results: T[];
}

// Reads a paged endpoint to the end, one request at a time. Sequential rather than
// parallel: the point of asking in pages is to keep each request small enough not to time
// out, which firing them all at once would undo.
//
// `complete` is false when the walk stopped without reaching `total` — the collection
// changed under it, or the server stopped returning rows. A caller that reads the result
// as a lookup table needs that, since it cannot otherwise tell an entry that does not
// exist from one the walk never reached.
export async function collectPages<T>(
    fetchPage: (skip: number, limit: number) => Promise<Page<T>>,
    pageSize: number,
): Promise<{ results: T[]; complete: boolean }> {
    const results: T[] = [];
    let expected: number | undefined;

    for (let skip = 0; ; skip += pageSize) {
        const page = await fetchPage(skip, pageSize);
        // The first page's total is the one the walk is measured against. A later page
        // answering with a different one means the collection changed underneath, and
        // neither total then describes what is in hand.
        if (expected === undefined) expected = page.total;
        results.push(...page.results);

        if (page.total !== expected) return { results, complete: false };
        if (results.length >= expected) return { results, complete: true };
        // Nothing came back but the total says there is more: stop rather than ask again
        // at an offset that will answer the same way.
        if (page.results.length === 0) return { results, complete: false };
    }
}

// The serial that scopes the H001 supporting-document folder (EOL-1714): prefer
// the serial carried from the list page's route/query state, falling back to the
// current installed-item selection for older links with no serial in the route.
export function resolveTargetSerial(
    serialFromRoute: string | undefined,
    installedItem: Pick<ComponentItem, 'serialNumber'> | undefined,
): string | null {
    return serialFromRoute ?? installedItem?.serialNumber ?? null;
}
